/**
 * Unified Dashboard v3.0 (Refactored)
 * Mobile-optimized, combined Home + My Tasks dashboard
 * Adapts to all industries and roles
 * 
 * REFACTORED: Components extracted to ./unified/ folder
 * - DashboardHeader.jsx - Header with user info and actions
 * - OverviewTab.jsx - Main overview content
 * - TasksTab.jsx - Task management
 * - TeamTab.jsx - Team management (owners/managers)
 * - InsightsTab.jsx - AI insights
 * - AddTaskDialog.jsx - Task creation modal
 * - DashboardCards.jsx - Reusable card components
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ActiveIntegrationBadges from '@/components/dashboards/ActiveIntegrationBadges';
import LocationsLeaderboard from '@/components/dashboards/LocationsLeaderboard';
import LocationDrawer from '@/components/dashboards/qsr/LocationDrawer';
import SpeedOfServiceModule from '@/components/dashboards/qsr/SpeedOfServiceModule';
import LabourSplhModule from '@/components/dashboards/qsr/LabourSplhModule';
import SalesMenuMixModule from '@/components/dashboards/qsr/SalesMenuMixModule';
import DeliveryModule from '@/components/dashboards/qsr/DeliveryModule';
import CorvanaAIModule from '@/components/dashboards/qsr/CorvanaAIModule';
import SpeedCoachModule from '@/components/dashboards/qsr/SpeedCoachModule';
import WasteVoidsModule from '@/components/dashboards/qsr/WasteVoidsModule';
import RoleHome from '@/components/dashboards/qsr/RoleHome';
import AutoTour from '@/components/dashboards/qsr/AutoTour';
import useQsrTasks from '@/components/dashboards/qsr/useQsrTasks';
import { isOverdue as qsrIsOverdue } from '@/components/dashboards/qsr/qsrTasks';
import { QSR_STORES } from '@/components/dashboards/qsr/qsrDemoData';
import { MapPin, Timer as TimerIcon, Bike, UtensilsCrossed, Gauge as GaugeIcon, Trash2 as Trash2Icon, Radio as RadioIcon } from 'lucide-react';
import { 
  Users, BarChart3, ListTodo, Brain, Loader2, Plus,
  Calendar, Award, Target, TrendingUp, CheckCircle2, Clock,
  Trophy, UserCircle, Activity, ChevronRight, Zap, Shield, Plug
} from 'lucide-react';
import { toast } from '@/utils/safeToast';
import { useClient } from '@/contexts/ClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardCustomization } from '@/contexts/DashboardCustomizationContext';
import { DashboardCustomizationPanel } from './DashboardCustomizationPanel';
import FocusWidget from './FocusWidget';
import { EnhancedTaskPanel } from './EnhancedTaskPanel';
import { ExpandableCardPanel } from './ExpandableCardPanel';
import { INDUSTRY_CONFIG, getIndustryConfig, ROLE_KPIS } from '@/config/dashboardConfig';
import { 
  generateDemoTasks, 
  generateRecommendations, 
  generateTeamMembers,
  OWNER_MANAGER_ROLES 
} from '@/config/industryRoleTasks';
import { 
  getDashboardMetrics, 
  getWinsOpportunities, 
  getBusinessSummary,
  getBusinessKpis,
  DEMO_BUSINESS_ID 
} from '@/services/unifiedMetricsApi';
import { apiClient } from '@/lib/apiClient';

// Import refactored components
import {
  DemoPreviewBanner,
  MobileQuickStats,
  OverviewTab,
  DraggableOverviewTab,
  TasksTab,
  CRMTab,
  IntegrationsTab,
  AddTaskDialog,
  KPICard,
  RecommendationCard,
  TeamMemberCard,
} from './unified';
import { GuidedOnboardingTour } from '@/components/onboarding/GuidedOnboardingTour';
// useOwnerTitle removed - owner titles are now derived from static config
import { DataRefreshNotifications } from '@/components/notifications/DataRefreshNotifications';
import useRealTimeDashboard from '@/hooks/useRealTimeDashboard';
import UnifiedDashboardHero from '@/components/dashboards/UnifiedDashboardHero';
import ProfileFocusChip from '@/components/dashboards/ProfileFocusChip';
import OnboardingIntentBanner from '@/components/OnboardingIntentBanner';
import UnconnectedModulesPrompt from '@/components/dashboards/UnconnectedModulesPrompt';
import LiveOrderFeed from '@/components/demo/LiveOrderFeed';
import { buildIndustrySignals } from '@/config/industrySignals';
import ClientHealthScore from './widgets/ClientHealthScore';
import WhatIfScenario from './widgets/WhatIfScenario';
import { devLog } from '@/lib/devLog';
import { API_URL } from '@/config/apiConfig';
import { authFetch } from '@/utils/authFetch';
import LocationScopeBar from '@/components/crm/LocationScopeBar';
import {
  CompetitorIntelligencePanel,
  RevenueForecastingWidget,
  StaffPerformanceLeaderboard,
  CustomerSentimentTracker,
  WeatherImpactWidget,
  SupplierPerformanceTracker,
  GoalTrackerWidget,
} from './widgets';

export default function UnifiedDashboard({ previewIndustry: propIndustry, previewContext: propPreview } = {}) {
  const navigate = useNavigate();
  const { clientType, customerBusiness, setClientType, setCustomerBusiness } = useClient();
  const { user} = useAuth();
  const { isWidgetEnabled } = useDashboardCustomization();
  
  // Demo preview context — a full context passed as a PROP (CRM "View Live"
  // inline dialog) always wins over the localStorage handoff (new-tab flow).
  // Reading the prop first avoids a mount race where UnifiedDashboard's state
  // initializer ran before the dialog's effect had written localStorage,
  // leaving demoPreview null and the dashboard rendering the operator's own
  // (empty) business instead of the previewed demo customer.
  const [lsPreview] = useState(() => {
    const demoContextStr = localStorage.getItem('demo_preview_context');
    if (demoContextStr) {
      try {
        const context = JSON.parse(demoContextStr);
        localStorage.removeItem('demo_preview_context');
        return context;
      } catch (e) {
        console.error('Error parsing demo preview context:', e);
      }
    }
    return null;
  });
  const demoPreview = propPreview || lsPreview;
  
  // Derived values - propIndustry takes highest priority (from CRM preview dialog)
  // Priority: CRM preview > demo preview > client context > user profile > default
  const previewIndustry = propIndustry?.toLowerCase() || demoPreview?.industry?.toLowerCase();
  const clientIndustry = customerBusiness?.industry?.toLowerCase();
  const userIndustry = user?.industry?.toLowerCase();
  const industry = previewIndustry || clientIndustry || userIndustry || 'general';
  
  // Get the correct role ID and name based on the effective industry
  // This ensures that even if clientType hasn't synced yet, we show correct labels
  const effectiveRoleId = clientType?.id || 'owner';
  const roleId = effectiveRoleId;
  
  // Get industry-specific role name for owner roles
  const getIndustryRoleName = () => {
    if (!clientType?.label) return 'Owner';
    
    // Check if this is an owner-level role that needs industry-specific naming
    const ownerRoleIds = ['owner', 'venue-operator', 'founder-ceo', 'practice-owner', 'store-owner', 'managing-partner', 'gym-owner', 'agency-principal', 'company-director', 'dealer-principal', 'school-director', 'salon-owner', 'factory-owner', 'firm-principal', 'firm-owner', 'hotel-owner', 'company-owner', 'business-owner', 'senior-partner', 'venue-owner', 'farm-owner', 'center-director', 'care-provider-owner', 'pharmacy-owner', 'agency-owner', 'print-shop-owner', 'pest-control-owner', 'moving-company-owner', 'cleaning-business-owner', 'pet-business-owner'];
    
    if (ownerRoleIds.includes(effectiveRoleId)) {
      const ownerTitles = {
        'hospitality': 'Venue Owner',
        'healthcare': 'Practice Principal',
        'technology': 'Founder/CEO',
        'it_services': 'Founder/CEO',
        'saas': 'Founder/CEO',
        'software': 'Founder/CEO',
        'retail': 'Store Owner',
        'wholesale': 'Owner / Director',
        'professional': 'Managing Director',
        'construction': 'Director',
        'fitness': 'Gym Owner',
        'property': 'Agency Principal',
        'automotive': 'Dealer Principal',
        'education': 'School Director',
        'beauty': 'Salon Owner',
        'manufacturing': 'Managing Director',
        'mining': 'Managing Director',
        'utilities': 'Managing Director',
        'admin_services': 'Managing Director',
        'public_admin': 'Director',
        'finance': 'Principal',
        'accounting': 'Practice Principal',
        'accommodation': 'Hotel Owner',
        'logistics': 'Business Owner',
        'trades': 'Business Owner',
        'legal': 'Senior Partner',
        'entertainment': 'Venue Owner',
        'agriculture': 'Farm Owner',
        'gaming': 'Gaming Venue Owner',
        'environmental': 'Company Director',
        'cleaning': 'Business Owner',
        'pet_services': 'Business Owner',
        'childcare': 'Center Director',
        'senior_care': 'Care Provider Owner',
        'pharmacy': 'Pharmacy Owner',
        'staffing': 'Agency Owner',
        'printing': 'Print Shop Owner',
        'pest_control': 'Business Owner',
        'moving_storage': 'Company Owner'
      };
      return ownerTitles[industry] || 'Business Owner';
    }
    return clientType?.label || 'Owner';
  };
  
  const roleName = getIndustryRoleName();
  // Preview/demo mode = CRM "view as" dialog (propIndustry) or demo tenant switcher.
  // Only in those modes does customerBusiness carry meaningful (demo) placeholders.
  const isPreviewMode = !!propIndustry || !!demoPreview;
  const userName = demoPreview?.demo_user_name || user?.first_name || 'User';
  const userBusinessName = demoPreview?.company_name || user?.company_name;
  // For a real logged-in user, their own company_name is the source of truth.
  // customerBusiness.name defaults to demo placeholders (e.g. "The Continental"),
  // so we only trust it in preview/demo mode — otherwise live users saw a demo
  // venue / their industry label instead of their actual business name.
  const businessName = isPreviewMode
    ? (userBusinessName || customerBusiness?.name || 'Your Business')
    : (user?.company_name || customerBusiness?.name || 'Your Business');
  const lastLogin = user?.last_login || null;
  const effectiveBusinessId = demoPreview?.business_id || user?.business_id || customerBusiness?.business_id || DEMO_BUSINESS_ID;
  const canSeeTeamView = OWNER_MANAGER_ROLES.includes(roleId);
  // Demo accounts (preview mode or seeded demo tenants) keep the rich sample
  // numbers. Real accounts must show their REAL business-metrics data.
  const isDemoAccount = !!demoPreview || user?.is_demo === true || effectiveBusinessId === DEMO_BUSINESS_ID;
  
  // Check if CRM module is enabled (via user modules or demo preview)
  const userModules = user?.modules || demoPreview?.modules || [];
  const hasCRMModule = userModules.some(m => 
    m?.toLowerCase().includes('crm') || 
    m?.toLowerCase().includes('customerinsights') ||
    m?.toLowerCase().includes('customer')
  ) || demoPreview || canSeeTeamView; // Show CRM tab for owners/managers or in demo mode
  
  // Integrations tab - Only for Owner and Senior Management roles
  const INTEGRATION_ALLOWED_ROLES = ['owner', 'venue-operator', 'founder-ceo', 'practice-owner', 'store-owner', 'managing-partner', 'gym-owner', 'agency-principal', 'company-director', 'dealer-principal', 'school-director', 'salon-owner', 'factory-owner', 'firm-principal', 'firm-owner', 'hotel-owner', 'company-owner', 'business-owner', 'senior-partner', 'venue-owner', 'farm-owner', 'general-manager', 'senior-manager', 'operations-director', 'cfo', 'coo', 'cto', 'managing-director'];
  const canManageIntegrations = INTEGRATION_ALLOWED_ROLES.includes(roleId) || 
    user?.permissions?.includes('manage_integrations') ||
    user?.role === 'owner' ||
    user?.role === 'admin';
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  // QSR demo module suite: dedicated tabs + store drill-down drawer
  const qsrDemo = isDemoAccount && industry === 'qsr';
  const [drawerStore, setDrawerStore] = useState(null);
  const [qsrRole, setQsrRole] = useState('owner');
  const [qsrScope, setQsrScope] = useState('southport');
  // QSR industry add-ons (switch-on innovations, billed per staff). Default on
  // so the demo shows the full experience; toggling gates optional modules.
  const [addons, setAddons] = useState({ forecast: true, waste: true, speedcoach: true, upsell: true });
  const toggleAddon = useCallback((k) => setAddons((a) => ({ ...a, [k]: !a[k] })), []);
  // Lift QSR task state so the Corvana AI tab badge + digest share it (enabled only for the QSR demo)
  const qsrTaskApi = useQsrTasks(effectiveBusinessId, qsrDemo);
  const qsrOverdue = qsrDemo ? qsrTaskApi.tasks.filter((t) => t.status !== 'done' && qsrIsOverdue(t)).length : 0;

  // ── Per-staff dashboard-area enforcement (Iter-422) ──────────────────────
  // Invited staff have a gated login: /api/staff/access/me returns the areas
  // (tabs) they're allowed to see. Owners/admins are ungated.
  // To avoid a flash-of-ungated-tabs, staff-role users start RESTRICTED
  // (overview/tasks only) until the async access check resolves; everyone
  // else (owners/managers/demo) defaults to ungated so their tabs never flicker.
  const _rolesLower = (user?.roles || []).map((r) => String(r).toLowerCase());
  const _looksLikeStaff =
    _rolesLower.includes('staff') &&
    !_rolesLower.some((r) => ['owner', 'admin', 'super_admin', 'manager'].includes(r));
  const [staffGate, setStaffGate] = useState(
    _looksLikeStaff ? { gated: true, areas: ['overview', 'tasks'] } : { gated: false, areas: null }
  );
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await apiClient.get('/api/staff/access/me');
        if (!active) return;
        if (data?.gated) setStaffGate({ gated: true, areas: data.areas || [] });
        else setStaffGate({ gated: false, areas: null });
      } catch { /* default: ungated */ }
    })();
    return () => { active = false; };
  }, []);
  const areaAllowed = useCallback(
    (area) => !staffGate.gated || (staffGate.areas || []).includes(area),
    [staffGate]
  );
  const showCRMTab = hasCRMModule && areaAllowed('crm');
  const showTeamTab = canSeeTeamView && areaAllowed('team');
  const showIntegrationsTab = canManageIntegrations && areaAllowed('integrations');
  const showInsightsTab = areaAllowed('insights');
  // If a gated staff member lands on a tab they can't access, fall back.
  useEffect(() => {
    if (!staffGate.gated) return;
    const allowed = { overview: true, tasks: areaAllowed('tasks'), crm: showCRMTab, team: showTeamTab, integrations: showIntegrationsTab, insights: showInsightsTab };
    if (!allowed[activeTab]) setActiveTab('overview');
  }, [staffGate, activeTab, areaAllowed, showCRMTab, showTeamTab, showIntegrationsTab, showInsightsTab]);

  // Deep-link: /app/dashboard?tab=<tab> opens that QSR tab (used by Cory suggestions from other pages).
  useEffect(() => {
    if (!qsrDemo) return;
    const t = new URLSearchParams(window.location.search).get('tab');
    const valid = ['overview', 'locations', 'labour', 'speed', 'speedcoach', 'sales', 'delivery', 'waste', 'insights'];
    if (t && valid.includes(t)) setActiveTab(t);
  }, [qsrDemo]);


  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium' });
  const [filter, setFilter] = useState('all');
  const [kpis, setKpis] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [liveMetrics, setLiveMetrics] = useState([]);
  // Real-data truth: which accounting/data sources are actually connected.
  // Empty for a real (non-demo) account that hasn't connected MYOB/Xero yet.
  const [dataSources, setDataSources] = useState(null);
  // Connection truth that resolves INSTANTLY from the user's integration list
  // (no network) and upgrades to the metrics `data_sources` once that fetch
  // lands. Prevents the hero/module-prompt from showing a false "no data source
  // connected" while the (slower) metrics call is still in flight.
  const effectiveConnectedSources = useMemo(() => {
    if (Array.isArray(dataSources) && dataSources.length) return dataSources;
    const fromUser = (user?.integrations || [])
      .filter((i) => ['connected', 'active'].includes(i?.status))
      .map((i) => i?.provider)
      .filter(Boolean);
    return fromUser;
  }, [dataSources, user]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Onboarding tour state - show for new users who haven't completed onboarding
  const shouldShowTour = user && !demoPreview && user.onboarding_completed === false;
  const [showOnboardingTour, setShowOnboardingTour] = useState(shouldShowTour);

  // Live (non-demo) clients must finish the Setup Guide before the tour starts.
  // setupReady: null = loading, false = show guide, true = ready (tour can run).
  const isLiveOnboarding = shouldShowTour && !isDemoAccount;
  const [setupReady, setSetupReady] = useState(null);
  useEffect(() => {
    if (!isLiveOnboarding) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API_URL}/api/account/setup-status`);
        if (!cancelled && r.ok) setSetupReady((await r.json()).ready);
      } catch { if (!cancelled) setSetupReady(true); /* fail-open: never trap the user */ }
    })();
    return () => { cancelled = true; };
  }, [isLiveOnboarding]);
  
  // Unified metrics state
  const [unifiedMetrics, setUnifiedMetrics] = useState(null);
  const [unifiedWins, setUnifiedWins] = useState([]);
  const [unifiedOpportunities, setUnifiedOpportunities] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  
  // Real-time dashboard updates for live metrics
  const { 
    metrics: realTimeMetrics, 
    isConnected: isRealTimeConnected,
    lastUpdate: realTimeLastUpdate 
  } = useRealTimeDashboard(effectiveBusinessId, {
    enabled: !demoPreview, // Disable for demo preview
    refreshInterval: 30000, // 30 seconds
    enablePolling: true
  });
  
  // Sync ClientContext with user's industry on initial login only
  // Does NOT re-sync when demo switcher changes the industry
  const hasSyncedIndustry = React.useRef(false);
  useEffect(() => {
    if (!hasSyncedIndustry.current && user?.industry) {
      hasSyncedIndustry.current = true;
      const savedIndustry = localStorage.getItem('corvana_selected_industry');
      // Only sync from user profile if no demo override exists in localStorage
      if (!savedIndustry && user.industry.toLowerCase() !== customerBusiness?.industry?.toLowerCase()) {
        setCustomerBusiness(prev => ({
          ...prev,
          industry: user.industry,
          name: user.company_name || prev.name
        }));
      }
    }
  }, [user?.industry, user?.company_name, customerBusiness?.industry, setCustomerBusiness]);
  
  // Fetch unified metrics
  const fetchUnifiedMetrics = useCallback(async () => {
    if (!effectiveBusinessId) return;
    // allSettled — a failure in wins/summary/kpis must NOT wipe out the metrics
    // (previously one rejected call sent the whole dashboard to a "disconnected"
    // state even though the metrics/data_sources came back fine).
    const [metricsR, winsR, summaryR, kpisR] = await Promise.allSettled([
      getDashboardMetrics(effectiveBusinessId, 'client'),
      getWinsOpportunities(effectiveBusinessId),
      getBusinessSummary(effectiveBusinessId, userName),
      getBusinessKpis(effectiveBusinessId, 'owner', industry),
    ]);
    const metricsRes = metricsR.status === 'fulfilled' ? metricsR.value : null;
    const winsRes = winsR.status === 'fulfilled' ? winsR.value : null;
    const summaryRes = summaryR.status === 'fulfilled' ? summaryR.value : null;
    const kpisRes = kpisR.status === 'fulfilled' ? kpisR.value : null;

    if (metricsRes?.success) setUnifiedMetrics(metricsRes.data);
    if (winsRes?.success) {
      setUnifiedWins(winsRes.wins || []);
      setUnifiedOpportunities(winsRes.opportunities || []);
    }
    if (summaryRes && summaryRes.success !== false) setSummaryData(summaryRes);

    // Truth about connected accounting/data sources (drives the no-data CTA).
    // NOTE: an empty array is truthy, so a naive `a || b` would lock in an
    // empty summary list and never fall through to the authoritative metrics
    // list. Prefer whichever is actually populated (metrics is source of truth).
    const metricSources = Array.isArray(metricsRes?.data?.data_sources) ? metricsRes.data.data_sources : [];
    const summarySources = Array.isArray(summaryRes?.data_sources) ? summaryRes.data_sources : [];
    setDataSources(metricSources.length ? metricSources : summarySources);

    // For REAL accounts, drive the KPI tiles from the live metrics API instead
    // of random demo figures. Demo accounts keep their generated sample KPIs.
    if (!isDemoAccount && kpisRes?.success && Array.isArray(kpisRes.kpis)) {
      setKpis(kpisRes.kpis.map((k, idx) => ({
        id: k.id || `kpi_${idx}`,
        name: k.name,
        value: k.value,
        target: k.target,
        change: k.change_pct ?? 0,
        trend: k.trend,
        source: k.source,
        industry,
      })));
    }
  }, [effectiveBusinessId, userName, industry, isDemoAccount]);
  
  // Generate KPIs based on role and industry
  const generateKPIs = useCallback(() => {
    const industryConfig = getIndustryConfig(industry);
    // Check if role is an owner-level role (includes all industry-specific owner IDs)
    const ownerRoleIds = ['owner', 'venue-operator', 'founder-ceo', 'practice-owner', 'store-owner', 'managing-partner', 'gym-owner', 'agency-principal', 'company-director', 'dealer-principal', 'school-director', 'salon-owner', 'factory-owner', 'firm-principal', 'firm-owner', 'hotel-owner', 'company-owner', 'business-owner', 'senior-partner', 'venue-owner', 'farm-owner', 'investor', 'advisor'];
    const isOwnerRole = ownerRoleIds.includes(roleId);
    const kpiNames = isOwnerRole ? industryConfig.kpis.slice(0, 4) : (ROLE_KPIS[roleId] || ROLE_KPIS.owner);
    
    const generated = kpiNames.map((name, idx) => {
      const base = 70 + Math.random() * 30;
      const trend = Math.floor(Math.random() * 20) - 5;
      const isPercentage = name.includes('%') || name.includes('Rate');
      const isCurrency = name.includes('Revenue') || name.includes('Profit') || name.includes('Cash') || 
                         name.includes('Sales') || name.includes('Billings') || name.includes('ADR') || 
                         name.includes('MRR') || name.includes('Value');
      const isTime = name.includes('Time') || name.includes('Days');
      
      let value, target;
      if (isPercentage) {
        value = `${Math.round(base)}%`;
        target = `${Math.round(base * 1.1)}%`;
      } else if (isCurrency) {
        value = `$${Math.round(base * 1000).toLocaleString()}`;
        target = `$${Math.round(base * 1.1 * 1000).toLocaleString()}`;
      } else if (isTime) {
        value = `${Math.round(base / 5)} min`;
        target = `${Math.round(base / 6)} min`;
      } else {
        value = Math.round(base);
        target = Math.round(base * 1.1);
      }
      
      return {
        id: `kpi_${idx}`,
        name,
        value,
        target,
        trend,
        progress: Math.min(Math.round((base / (base * 1.1)) * 100), 100),
        sparkline: Array(7).fill(0).map(() => ({ value: Math.round(base + (Math.random() - 0.5) * 10) })),
        industry
      };
    });
    setKpis(generated);
  }, [roleId, industry]);
  
  // Initialize data
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      // Demo accounts get a rich generated showcase. Real accounts NEVER get
      // fabricated tasks/recommendations/team/metrics — those stay empty until
      // live data is wired, so sections fall back to their connect/empty states.
      const newTasks = isDemoAccount ? (generateDemoTasks(roleId, industry) || []) : [];
      const newRecs = isDemoAccount ? (generateRecommendations(roleId, industry) || []) : [];
      const newTeamMembers = (isDemoAccount && canSeeTeamView ? generateTeamMembers(industry) : []) || [];
      const industryConfig = getIndustryConfig(industry);
      const metrics = isDemoAccount
        ? (industryConfig.metrics || ['Metric 1', 'Metric 2', 'Metric 3', 'Metric 4']).map(
            (name) => ({ name, value: Math.floor(50 + Math.random() * 100) })
          )
        : [];
      
      setTasks(newTasks);
      setRecommendations(newRecs);
      setTeamMembers(newTeamMembers);
      setLiveMetrics(metrics);
      // Random sample KPIs only for demo accounts; real accounts get live KPIs
      // from fetchUnifiedMetrics (dedicated effect below).
      if (isDemoAccount) generateKPIs();
      setLoading(false);
    });
    
    return () => cancelAnimationFrame(frameId);
  }, [roleId, industry, generateKPIs, canSeeTeamView, isDemoAccount]);

  // Live metrics fetch runs in its OWN clean effect keyed only on the business
  // id — NOT bundled into the churny rAF setup effect above (whose deps flip
  // during mount and were aborting the in-flight metrics request, leaving the
  // dashboard stuck on a false "no data source connected" state).
  useEffect(() => {
    if (!effectiveBusinessId) return;
    fetchUnifiedMetrics();
    // Keyed only on the business id on purpose — fetchUnifiedMetrics changes
    // identity as userName/industry resolve during mount, and including it here
    // re-fired the 4-call fetch repeatedly (aborting in-flight requests and
    // delaying the live state by ~14s). One fetch per business is enough.
  }, [effectiveBusinessId]);
  
  // Task handlers
  const toggleTask = (taskId) => {
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, status: t.status === 'completed' ? 'pending' : 'completed' } : t
    ));
    const task = tasks.find(t => t.id === taskId);
    toast.success(task?.status === 'completed' ? 'Task reopened' : 'Task completed!');
  };
  
  const addTask = () => {
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    const task = {
      id: `task_${Date.now()}`,
      title: newTask.title,
      priority: newTask.priority,
      status: 'pending',
      due: 'Today',
      created_at: new Date().toISOString()
    };
    setTasks(prev => [task, ...prev]);
    setAddTaskOpen(false);
    setNewTask({ title: '', priority: 'medium' });
    toast.success('Task added!');
  };
  
  // Counts
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const pendingCount = tasks.filter(t => t.status !== 'completed').length;
  const highPriorityCount = tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20 sm:pb-8" data-testid="unified-dashboard">
      <LocationScopeBar businessId={effectiveBusinessId} />

      {/* Real-time Data Refresh Notifications */}
      <DataRefreshNotifications userId={user?.id} enabled={!!user?.id} />

      {/* Guided Onboarding Tour — only after setup is ready (live clients) */}
      {showOnboardingTour && (!isLiveOnboarding || setupReady === true) && (
        <GuidedOnboardingTour
          onComplete={() => setShowOnboardingTour(false)}
          onSkip={() => setShowOnboardingTour(false)}
        />
      )}
      
      {/* Demo Preview Banner */}
      <DemoPreviewBanner 
        demoPreview={demoPreview} 
        onClose={() => window.close()} 
      />

      {/* iter-390 — Single, calm, light, industry-themed hero. Merges the old
          dark WelcomeHero + DashboardHeader + AIIntelligenceHero greeting strip
          into ONE block. Greeting = "Good {tod}, {firstName}"; business name
          underneath; colours driven by the industry field. Tabs sit directly
          beneath (below). */}
      <UnifiedDashboardHero
        userName={isPreviewMode ? userName : (user?.first_name ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}` : userName)}
        businessName={businessName}
        industry={industry}
        connectedSources={isDemoAccount ? ((dataSources && dataSources.length) ? dataSources : ['xero', 'square', 'google_analytics']) : effectiveConnectedSources}
        signals={buildIndustrySignals(
          industry,
          (() => {
            const m = isDemoAccount
              ? { coversToday: 142, bookingsToday: 38, ordersToday: 210, fulfilPending: 12, revenueToday: 18500, appointmentsToday: 24, waitlistCount: 6, classesToday: 9, studentsEnrolled: 320, jobsActive: 14, crewDeployed: 32, listingsActive: 28, inspectionsToday: 7, guestsInHouse: 86, kpiScore: 88 }
              : (unifiedMetrics || realTimeMetrics || {});
            return {
              coversToday: m.coversToday,
              bookingsToday: m.bookingsToday,
              ordersToday: m.ordersToday,
              fulfilPending: m.fulfilPending,
              revenueToday: m.revenueToday,
              appointmentsToday: m.appointmentsToday,
              waitlistCount: m.waitlistCount,
              classesToday: m.classesToday,
              studentsEnrolled: m.studentsEnrolled,
              jobsActive: m.jobsActive,
              crewDeployed: m.crewDeployed,
              listingsActive: m.listingsActive,
              inspectionsToday: m.inspectionsToday,
              guestsInHouse: m.guestsInHouse,
              kpiScore: m.kpiScore,
            };
          })(),
          {
            completedCount: completedCount || 0,
            pendingCount: pendingCount || 0,
            highPriorityCount: highPriorityCount || 0,
          },
        )}
        cta={qsrDemo ? { label: 'Open morning briefing', onClick: () => setActiveTab('insights') } : (() => {
          const hour = new Date().getHours();
          if (highPriorityCount > 3) {
            return { label: 'Clear your backlog', onClick: () => window.scrollTo({ top: 400, behavior: 'smooth' }) };
          }
          if (hour < 10) {
            return { label: 'Open morning briefing', onClick: () => { window.location.hash = '#morning-briefing'; } };
          }
          if (hour >= 16 && hour < 20) {
            return { label: 'Wrap up today', onClick: () => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }) };
          }
          return null;
        })()}
        onRefresh={() => { 
          if (isDemoAccount) { generateKPIs(); setTasks(generateDemoTasks(roleId, industry)); }
          else { fetchUnifiedMetrics(); }
        }}
        onAddTask={() => setAddTaskOpen(true)}
      />

      {/* Priority survey — directly under the greeting (per product order):
          greeting → "what's your biggest priority?" → connect → modules. */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8">
        <OnboardingIntentBanner />
      </div>

      {/* Connect-your-data CTA — shown for real accounts with no accounting
          source yet. Broad "Connect your accounts" wording (any provider). */}
      {!isDemoAccount && Array.isArray(dataSources) && dataSources.length === 0 && (
        <Card className="border-amber-300 bg-amber-50/70" data-testid="connect-data-banner">
          <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <Plug className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">Connect your accounts to see real numbers</p>
                <p className="text-sm text-amber-800/80 mt-0.5">
                  Your dashboard is showing $0 because no accounting source is connected yet. Connect your accounting software to pull in your real revenue, profit, cash position and invoices.
                </p>
              </div>
            </div>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              onClick={() => navigate('/app/setup/connect')}
              data-testid="connect-data-cta"
            >
              Connect your accounts
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Enabled-but-not-connected modules prompt — front & centre so clients
          get the most out of modules they've turned on. */}
      <UnconnectedModulesPrompt
        enabledModules={userModules}
        connectedSources={effectiveConnectedSources}
        isDemo={isDemoAccount}
        onConnect={() => navigate('/app/integrations')}
      />

      {/* Gentle nudge toward the private My Focus profile (self-contained). */}
      {!qsrDemo && <ProfileFocusChip />}
      
      {/* Dashboard Customization Panel */}
      <DashboardCustomizationPanel />
      
      {/* Focus Widget — Single priority task with adaptive coaching */}
      <FocusWidget />
      
      {/* Mobile Quick Stats */}
      <MobileQuickStats
        completedCount={completedCount}
        pendingCount={pendingCount}
        highPriorityCount={highPriorityCount}
        onAddTask={() => setAddTaskOpen(true)}
      />
      
      {/* QSR integration trust badges (pulsing green = connected) */}
      {isDemoAccount && (
        <div className="mb-4">
          <ActiveIntegrationBadges connected={demoPreview?.connected_integrations} />
        </div>
      )}

      {/* QSR store drill-down pop-out (opened from leaderboard / module rows) */}
      {qsrDemo && (
        <LocationDrawer store={drawerStore} open={!!drawerStore} onOpenChange={(o) => { if (!o) setDrawerStore(null); }} />
      )}
      {qsrDemo && <AutoTour onNavigate={setActiveTab} onSetRole={setQsrRole} />}
      {qsrDemo && <LiveOrderFeed enabled={qsrDemo} />}

      {/* QSR multi-store league table moved into the dedicated Locations tab */}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-tour="dashboard-overview">
        <TabsList className={`${qsrDemo
          ? 'flex flex-nowrap sm:flex-wrap gap-1 overflow-x-auto sm:overflow-x-visible'
          : `grid ${
            showCRMTab && showTeamTab && showIntegrationsTab ? 'grid-cols-6' :
            (showCRMTab && showTeamTab) || (showCRMTab && showIntegrationsTab) || (showTeamTab && showIntegrationsTab) ? 'grid-cols-5' :
            showCRMTab || showTeamTab || showIntegrationsTab ? 'grid-cols-4' : 'grid-cols-3'
          }`
        } w-full mb-4 sticky top-2 z-20 backdrop-blur-xl bg-background/80 supports-[backdrop-filter]:bg-background/60 rounded-xl shadow-sm`}>
          {qsrDemo ? (
            <>
              <TabsTrigger value="overview" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-overview"><BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>Overview</span></TabsTrigger>
              <TabsTrigger value="locations" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-locations"><MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>Locations</span></TabsTrigger>
              <TabsTrigger value="labour" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-labour"><GaugeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Labour &amp; SPLH</span><span className="sm:hidden">Labour</span></TabsTrigger>
              <TabsTrigger value="speed" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-speed"><TimerIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Speed of Service</span><span className="sm:hidden">Speed</span></TabsTrigger>
              {addons.speedcoach && (
                <TabsTrigger value="speedcoach" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-speedcoach"><RadioIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Speed Coach</span><span className="sm:hidden">Coach</span></TabsTrigger>
              )}
              <TabsTrigger value="sales" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-sales"><UtensilsCrossed className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Sales &amp; Menu</span><span className="sm:hidden">Sales</span></TabsTrigger>
              <TabsTrigger value="delivery" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-delivery"><Bike className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>Delivery</span></TabsTrigger>
              {addons.waste && (
                <TabsTrigger value="waste" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-waste"><Trash2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>Waste</span></TabsTrigger>
              )}
              <TabsTrigger value="insights" className="text-xs sm:text-sm gap-1 sm:gap-2 flex-none sm:flex-1 whitespace-nowrap" data-testid="tab-insights"><Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>Corvana AI</span>{qsrOverdue > 0 && (<span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" data-testid="insights-overdue-badge">{qsrOverdue}</span>)}</TabsTrigger>
            </>
          ) : (
            <>
          <TabsTrigger value="overview" className="text-xs sm:text-sm gap-1 sm:gap-2">
            <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Overview</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="text-xs sm:text-sm gap-1 sm:gap-2" data-tour="operations">
            <ListTodo className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Tasks</span>
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{pendingCount}</Badge>
            )}
          </TabsTrigger>
          {showCRMTab && (
            <TabsTrigger value="crm" className="text-xs sm:text-sm gap-1 sm:gap-2" data-tour="crm">
              <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>CRM</span>
            </TabsTrigger>
          )}
          {showTeamTab && (
            <TabsTrigger value="team" className="text-xs sm:text-sm gap-1 sm:gap-2" data-tour="operations">
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Team</span>
            </TabsTrigger>
          )}
          {showIntegrationsTab && (
            <TabsTrigger value="integrations" className="text-xs sm:text-sm gap-1 sm:gap-2">
              <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Integrations</span>
              <span className="sm:hidden">Apps</span>
            </TabsTrigger>
          )}
          {showInsightsTab && (
            <TabsTrigger value="insights" className="text-xs sm:text-sm gap-1 sm:gap-2" data-tour="intelligence">
              <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Corvana AI</span>
            </TabsTrigger>
          )}
            </>
          )}
        </TabsList>
        {/* QSR module tabs */}
        {qsrDemo && (
          <>
            <TabsContent value="locations" className="mt-0">
              <div className="qsr-stage p-3 sm:p-5">
                <LocationsLeaderboard stores={QSR_STORES} onSelectStore={setDrawerStore} />
              </div>
            </TabsContent>
            <TabsContent value="labour" className="mt-0">
              <LabourSplhModule onSelectStore={setDrawerStore} onNavigate={setActiveTab} />
            </TabsContent>
            <TabsContent value="speed" className="mt-0">
              <SpeedOfServiceModule onSelectStore={setDrawerStore} onNavigate={setActiveTab} />
            </TabsContent>
            {addons.speedcoach && (
              <TabsContent value="speedcoach" className="mt-0">
                <SpeedCoachModule />
              </TabsContent>
            )}
            <TabsContent value="sales" className="mt-0">
              <SalesMenuMixModule forecastEnabled={addons.forecast} onNavigate={setActiveTab} />
            </TabsContent>
            <TabsContent value="delivery" className="mt-0">
              <DeliveryModule onSelectStore={setDrawerStore} onNavigate={setActiveTab} />
            </TabsContent>
            {addons.waste && (
              <TabsContent value="waste" className="mt-0">
                <WasteVoidsModule onSelectStore={setDrawerStore} />
              </TabsContent>
            )}
            <TabsContent value="insights" className="mt-0">
              <CorvanaAIModule onSelectStore={setDrawerStore} addons={addons} onToggleAddon={toggleAddon} businessId={effectiveBusinessId} taskApi={qsrTaskApi} />
            </TabsContent>
          </>
        )}
        
        {/* Overview Tab - Uses DraggableOverviewTab for owners, standard OverviewTab for staff */}
        <TabsContent value="overview" className="mt-0">
          {qsrDemo && <RoleHome onNavigate={setActiveTab} onSelectStore={setDrawerStore} roleId={qsrRole} storeId={qsrScope} onRoleChange={setQsrRole} onStoreChange={setQsrScope} />}
          {canSeeTeamView ? (
            <DraggableOverviewTab
              userName={userName}
              businessId={effectiveBusinessId}
              businessName={businessName}
              industry={industry}
              lastLogin={lastLogin}
              canSeeTeamView={canSeeTeamView}
              unifiedMetrics={unifiedMetrics}
              unifiedWins={unifiedWins}
              unifiedOpportunities={unifiedOpportunities}
              summaryData={summaryData}
              kpis={kpis}
              tasks={tasks}
              recommendations={recommendations}
              liveMetrics={liveMetrics}
              completedCount={completedCount}
              pendingCount={pendingCount}
              highPriorityCount={highPriorityCount}
              isWidgetEnabled={isWidgetEnabled}
              onToggleTask={toggleTask}
              onExpandTask={(t) => setSelectedTask(t)}
              onExpandCard={setExpandedCard}
              setActiveTab={setActiveTab}
              setFilter={setFilter}
              isDemoAccount={isDemoAccount}
              dataSources={effectiveConnectedSources}
            />
          ) : (
            <OverviewTab
              userName={userName}
              businessId={effectiveBusinessId}
              businessName={businessName}
              industry={industry}
              lastLogin={lastLogin}
              canSeeTeamView={canSeeTeamView}
              unifiedMetrics={unifiedMetrics}
              unifiedWins={unifiedWins}
              unifiedOpportunities={unifiedOpportunities}
              summaryData={summaryData}
              kpis={kpis}
              tasks={tasks}
              recommendations={recommendations}
              liveMetrics={liveMetrics}
              completedCount={completedCount}
              pendingCount={pendingCount}
              highPriorityCount={highPriorityCount}
              isWidgetEnabled={isWidgetEnabled}
              onToggleTask={toggleTask}
              onExpandTask={(t) => setSelectedTask(t)}
              onExpandCard={setExpandedCard}
              setActiveTab={setActiveTab}
              setFilter={setFilter}
              isDemoAccount={isDemoAccount}
              dataSources={effectiveConnectedSources}
            />
          )}
        </TabsContent>
        
        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-0">
          <TasksTab
            tasks={tasks}
            filter={filter}
            setFilter={setFilter}
            pendingCount={pendingCount}
            onToggleTask={toggleTask}
            onExpandTask={(t) => setSelectedTask(t)}
            onAddTask={() => setAddTaskOpen(true)}
          />
        </TabsContent>
        
        {/* CRM Tab - Only when CRM module is enabled */}
        {hasCRMModule && (
          <TabsContent value="crm" className="mt-0">
            <CRMTab
              businessName={businessName}
              industry={industry}
              userName={userName}
            />
          </TabsContent>
        )}
        
        {/* Team Tab - Only for managers/owners */}
        {canSeeTeamView && (
          <TabsContent value="team" className="mt-0 space-y-4" data-testid="team-view-tab">
            {/* Team Overview Stats */}
            <div className="flex items-center justify-between mb-4">
              <Badge 
                className={`bg-gradient-to-r ${getIndustryConfig(industry)?.gradient || 'from-slate-500 to-gray-500'} text-white border-0`}
              >
                <Users className="w-3 h-3 mr-1" />
                Team Overview
              </Badge>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/app/staff-performance')}>
                  <BarChart3 className="w-4 h-4 mr-1" />
                  Performance Reports
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/app/staff-permissions')}>
                  Manage Permissions <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
            
 <div className="grid grid-cols-1 gap-3">
              <Card className="glass-card cursor-pointer hover:shadow-lg transition-all group" onClick={() => navigate('/app/staff-performance')}>
                <CardContent className="p-3 sm:p-4 text-center">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 text-blue-500 group-hover:scale-110 transition-transform" />
                  <p className="text-xl sm:text-2xl font-display font-bold">{teamMembers.length}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Team Members</p>
                </CardContent>
              </Card>
              <Card className="glass-card cursor-pointer hover:shadow-lg transition-all group" onClick={() => navigate('/app/staff-performance')}>
                <CardContent className="p-3 sm:p-4 text-center">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 text-emerald-500 group-hover:scale-110 transition-transform" />
                  <p className="text-xl sm:text-2xl font-display font-bold">{teamMembers.length > 0 ? Math.round(teamMembers.reduce((a, m) => a + m.performance, 0) / teamMembers.length) : 0}%</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Avg Performance</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-3 sm:p-4 text-center">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 text-emerald-500" />
                  <p className="text-xl sm:text-2xl font-display font-bold">{teamMembers.reduce((a, m) => a + (m.tasksCompleted || 0), 0)}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Tasks Done</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-3 sm:p-4 text-center">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 text-amber-500" />
                  <p className="text-xl sm:text-2xl font-display font-bold">{teamMembers.reduce((a, m) => a + (m.tasksPending || 0), 0)}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Pending</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Performance Distribution */}
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  Team Performance Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-xs">Exceeding ({teamMembers.filter(m => m.status === 'exceeding').length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-xs">On Track ({teamMembers.filter(m => m.status === 'on-track').length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-xs">At Risk ({teamMembers.filter(m => m.status === 'at-risk').length})</span>
                  </div>
                </div>
                <div className="h-4 w-full rounded-full overflow-hidden flex bg-slate-100">
                  {teamMembers.length > 0 && (
                    <>
                      <div className="bg-emerald-500 h-full" style={{ width: `${(teamMembers.filter(m => m.status === 'exceeding').length / teamMembers.length) * 100}%` }} />
                      <div className="bg-blue-500 h-full" style={{ width: `${(teamMembers.filter(m => m.status === 'on-track').length / teamMembers.length) * 100}%` }} />
                      <div className="bg-amber-500 h-full" style={{ width: `${(teamMembers.filter(m => m.status === 'at-risk').length / teamMembers.length) * 100}%` }} />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Team Members Grid */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <UserCircle className="w-4 h-4" />
                Team Members
              </h3>
 <div className="grid grid-cols-1 gap-3">
                {teamMembers.map(member => (
                  <TeamMemberCard 
                    key={member.id} 
                    member={member}
                    onClick={(m) => toast.info(`Viewing ${m.name}'s dashboard...`)}
                  />
                ))}
              </div>
            </div>
            
            {/* Quick Actions for Team */}
            <Card className="glass-card bg-gradient-to-br from-blue-50/50 to-indigo-50/50 border-blue-200/50">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="text-xs">
                    <Calendar className="w-3.5 h-3.5 mr-1.5" />
                    Schedule Check-in
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs">
                    <Award className="w-3.5 h-3.5 mr-1.5" />
                    Send Recognition
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs">
                    <Target className="w-3.5 h-3.5 mr-1.5" />
                    Set Team Goal
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Integrations Tab - Only for Owner/Senior Management */}
        {canManageIntegrations && (
          <TabsContent value="integrations" className="mt-0">
            <IntegrationsTab />
          </TabsContent>
        )}
        
        {/* Insights Tab (generic — QSR demo uses CorvanaAIModule above) */}
        {!qsrDemo && (
        <TabsContent value="insights" className="mt-0 space-y-4">
          {/* Health Score + What-If Side by Side */}
          {(isWidgetEnabled('health-score') || isWidgetEnabled('what-if')) && (
            <div className="grid grid-cols-1 gap-4">
              {isWidgetEnabled('health-score') && <ClientHealthScore industry={industry} />}
              {isWidgetEnabled('what-if') && <WhatIfScenario industry={industry} />}
            </div>
          )}

          {/* Revenue Forecasting (full width) */}
          {isWidgetEnabled('insights-revenue-forecast') && <RevenueForecastingWidget industry={industry} />}

          {/* Goals + Weather Side by Side */}
          {(isWidgetEnabled('insights-goals') || isWidgetEnabled('insights-weather')) && (
            <div className="grid grid-cols-1 gap-4">
              {isWidgetEnabled('insights-goals') && <GoalTrackerWidget industry={industry} />}
              {isWidgetEnabled('insights-weather') && <WeatherImpactWidget industry={industry} />}
            </div>
          )}

          {/* Competitor + Sentiment Side by Side */}
          {(isWidgetEnabled('insights-competitors') || isWidgetEnabled('insights-sentiment')) && (
            <div className="grid grid-cols-1 gap-4">
              {isWidgetEnabled('insights-competitors') && <CompetitorIntelligencePanel industry={industry} />}
              {isWidgetEnabled('insights-sentiment') && <CustomerSentimentTracker industry={industry} />}
            </div>
          )}

          {/* Supplier + Staff Performance Side by Side */}
          {(isWidgetEnabled('insights-suppliers') || isWidgetEnabled('insights-staff-leaderboard')) && (
            <div className="grid grid-cols-1 gap-4">
              {isWidgetEnabled('insights-suppliers') && <SupplierPerformanceTracker industry={industry} />}
              {isWidgetEnabled('insights-staff-leaderboard') && <StaffPerformanceLeaderboard industry={industry} />}
            </div>
          )}

          <Card className="glass-card bg-gradient-to-br from-amber-50/50 to-orange-50/50 border-amber-200/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-amber-600" />
                Corvana AI Insights
              </CardTitle>
              <CardDescription>
                Personalized recommendations based on your role and business data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {recommendations.map((rec, idx) => (
                <RecommendationCard key={rec.id || rec.title || `rec-${idx}`} rec={rec} />
              ))}
            </CardContent>
          </Card>
          
          {/* Performance Summary */}
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
 <div className="grid grid-cols-1 gap-3">
                {kpis.map(kpi => (
                  <KPICard key={kpi.id} kpi={kpi} compact />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
      
      {/* Add Task Dialog */}
      <AddTaskDialog
        open={addTaskOpen}
        onOpenChange={setAddTaskOpen}
        newTask={newTask}
        setNewTask={setNewTask}
        onAddTask={addTask}
      />
      
      {/* Mobile FAB */}
      <div className="fixed bottom-20 right-4 sm:hidden z-50">
        <Button 
          size="lg" 
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => setAddTaskOpen(true)}
        >
          <Plus className="w-6 h-6" />
        </Button>
      </div>
      
      {/* Enhanced Task Panel */}
      {selectedTask && (
        <EnhancedTaskPanel 
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onComplete={(taskId) => {
            toggleTask(taskId);
            setSelectedTask(null);
          }}
        />
      )}
      
      {/* Expandable Card Panel */}
      {expandedCard && (
        <ExpandableCardPanel 
          cardType={expandedCard}
          onClose={() => setExpandedCard(null)}
        />
      )}
    </div>
  );
}
